import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAccountData } from '../redux/main/accountSlice';
import { useColorPalette } from '../hooks/useColorPalette';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faComment,
  faImage,
  faWrench,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import WizardHeader from '../components/WizardHeader';

import CustomerSelect from '../components/CustomerSelect';
import { apiService } from '../utils/apiService';
import { toast } from 'react-toastify';
import { formatDurationMinutes, formatDistance } from '../utils/formatUtils.js';
import AssistanceTypeServices, { sortAssistanceTypes } from '../components/assistanceRequestsPage/AssistanceTypeServices';

// Componenti per i vari step del wizard
// Servizi scelti per l'assistenza: la lista tipiSistema oppure, per le richieste con un solo
// servizio, tipoSistema (richiesta #018)
const getSelectedSystemTypes = (formData) => {
  if (Array.isArray(formData.tipiSistema) && formData.tipiSistema.length > 0) return formData.tipiSistema;
  return formData.tipoSistema ? [formData.tipoSistema] : [];
};

// Un servizio può arrivare con l'_id della riga dell'impianto o con il typeId dell'anagrafica
const isSameSystemType = (a, b) => {
  if (!a || !b) return false;
  const idsA = [a._id, a.typeId].filter(Boolean).map(String);
  const idsB = [b._id, b.typeId].filter(Boolean).map(String);
  return idsA.some(id => idsB.includes(id));
};

// Servizi salvati sulla richiesta (systemTypes), nel formato usato dalla pagina
const systemTypesFromRequest = (requestData) => {
  if (!Array.isArray(requestData.systemTypes) || requestData.systemTypes.length === 0) return undefined;
  return requestData.systemTypes.map(t => ({
    _id: t.typeId,
    typeId: t.typeId,
    typeLabel: t.typeLabel,
    expirationWarrantyDate: t.expirationWarrantyDate
  }));
};

const ClienteStep = ({ formData, setFormData, nextStep }) => {
  const colors = useColorPalette(); // Hook per i colori del tema
  const token = localStorage.getItem('installamelo-auth-token');
  const accountData = useSelector(selectAccountData);

  // Inizializzare gli stati con i valori esistenti in formData, se presenti
  const [selectedCustomer, setSelectedCustomer] = useState(() => {
    return formData.cliente && Object.keys(formData.cliente).length > 0 
      ? { 
          customerId: formData.cliente.id, 
          customerLabel: formData.cliente.nome || formData.cliente.label, 
          value: formData.cliente.id || formData.cliente.value,
          label: formData.cliente.nome || formData.cliente.label,
          data: formData.cliente.data || formData.cliente 
        }
      : null;
  });
  
  // Flag per il primo caricamento in modalità edit
  const initialEditLoadRef = useRef(true);
  
  // Quando formData cambia, aggiorna anche lo stato locale (solo per il caricamento iniziale in modalità edit)
  useEffect(() => {
    if (formData.isEditMode && formData.cliente && 
        Object.keys(formData.cliente).length > 0 && 
        initialEditLoadRef.current) {
      
      console.log('Aggiornamento iniziale selectedCustomer da formData:', formData.cliente);
      
      setSelectedCustomer({ 
        customerId: formData.cliente.id, 
        customerLabel: formData.cliente.nome || formData.cliente.label, 
        value: formData.cliente.id || formData.cliente.value,
        label: formData.cliente.nome || formData.cliente.label,
        data: formData.cliente.data || formData.cliente 
      });
      
      prevCustomerRef.current = formData.cliente.id;
      
      initialEditLoadRef.current = false;
    }
  }, [formData.cliente, formData.isEditMode]);

  const prevCustomerRef = useRef(formData.cliente?.id || null);
  
  useEffect(() => {
    if (!selectedCustomer) return;
    
    if (formData.isEditMode && initialEditLoadRef.current) return;
    
    const isCustomerChanged = prevCustomerRef.current !== null && 
                             prevCustomerRef.current !== selectedCustomer.customerId;
    
    console.log('Verifica cambio cliente:', { 
      prev: prevCustomerRef.current, 
      current: selectedCustomer.customerId,
      isChanged: isCustomerChanged,
      isEditMode: formData.isEditMode,
      isFirstLoad: initialEditLoadRef.current
    });
    
    if (isCustomerChanged) {
      console.log('Cliente cambiato da', prevCustomerRef.current, 'a', selectedCustomer.customerId);
      setFormData({
        isEditMode: formData.isEditMode,
        cliente: {
          id: selectedCustomer.customerId,
          nome: selectedCustomer.customerLabel,
          ...selectedCustomer.data
        }
      });
      toast.info('Cliente cambiato. Tutti i dati della richiesta sono stati reimpostati.');
    } else if (!formData.isEditMode || !initialEditLoadRef.current) {
      setFormData(prev => ({
        ...prev,
        cliente: {
          id: selectedCustomer.customerId,
          nome: selectedCustomer.customerLabel,
          ...selectedCustomer.data
        }
      }));
    }
    
    prevCustomerRef.current = selectedCustomer.customerId;
  }, [selectedCustomer, formData.isEditMode, setFormData]);

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-6">Seleziona il Cliente</h2>
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cerca e seleziona un cliente
        </label>
        <CustomerSelect 
          selectedOption={selectedCustomer} 
          setSelectedOption={setSelectedCustomer}
          placeholder="Cerca cliente per nome, codice fiscale o partita IVA"
        />
        
        {selectedCustomer && (
          <div className="mt-6 p-4 bg-blue-50 rounded-md border border-blue-200">
            <div className="flex items-center mb-3">
              <i className="fa-duotone fa-user text-blue-500 mr-2"></i>
              <h3 className="text-lg font-medium">Cliente selezionato</h3>
            </div>
            
            <p className="font-medium text-lg">{selectedCustomer.label}</p>
            
            {selectedCustomer.data?.vatNumber && (
              <p className="text-sm text-gray-600 mt-1">
                <strong>P.IVA:</strong> {selectedCustomer.data.vatNumber}
              </p>
            )}
            
            {selectedCustomer.data?.addresses && selectedCustomer.data.addresses.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <strong>Indirizzo principale:</strong> {selectedCustomer.data.addresses[0].addressLabel}
                </p>
              </div>
            )}
            
            {selectedCustomer.data?.contactsList && selectedCustomer.data.contactsList.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <strong>Contatto:</strong> {selectedCustomer.data.contactsList[0].name} {selectedCustomer.data.contactsList[0].surname}
                  {selectedCustomer.data.contactsList[0].mobile && ` - ${selectedCustomer.data.contactsList[0].mobile}`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="flex justify-end">
        <button 
          style={{
            backgroundColor: !selectedCustomer ? '#94a3b8' : colors.primary || '#3a5a40',
            color: '#ffffff',
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            transition: 'background-color 0.3s',
            opacity: !selectedCustomer ? 0.7 : 1
          }}
          onClick={nextStep}
          disabled={!selectedCustomer}
        >
          Avanti
          <i className="fa-solid fa-chevron-right ml-2"></i>
        </button>
      </div>
    </div>
  );
};

const SegnalazioneStep = ({ formData, setFormData, nextStep, prevStep, comeFromStep3 }) => {
  const colors = useColorPalette(); // Hook per i colori del tema
  const [segnalazione, setSegnalazione] = useState(formData.segnalazione || '');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(formData.sistema || null);
  // Dall'ufficio si possono scegliere più servizi (richiesta #018): tipiSistema è la lista,
  // tipoSistema resta il primo per compatibilità
  const [selectedSystemTypes, setSelectedSystemTypes] = useState(getSelectedSystemTypes(formData));
  const [selectedUseCases, setSelectedUseCases] = useState(formData.useCases || []);
  const timerRef = useRef(null);
  const accountData = useSelector(selectAccountData);
  
  // Fetch customer systems when cliente is selected
  useEffect(() => {
    if (formData.cliente && formData.cliente.id) {
      fetchCustomerSystems();
    }
  }, [formData.cliente]);
  
  // Fetch customer systems
  const fetchCustomerSystems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('installamelo-auth-token');
      const url = `${import.meta.env.VITE_API_BASE_URL}/systems/systemsList?accountId=${accountData._id}&customerId=${formData.cliente.id}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Errore nel caricamento degli impianti');
      }
      
      const data = await response.json();
      setSystems(data);
    } catch (error) {
      console.error('Errore nel caricamento degli impianti:', error);
      toast.error('Errore nel caricamento degli impianti');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if warranty is expired
  const isWarrantyExpired = (warrantyExpirationDate) => {
    if (!warrantyExpirationDate) return true;
    const currentDate = new Date();
    const expirationDate = new Date(warrantyExpirationDate);
    return currentDate > expirationDate;
  };
  
  // Format date to DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getRelevanceColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Handle system selection
  const handleSystemSelect = (system) => {
    // Make sure we have a valid system with an ID
    if (!system || !system._id) {
      console.error('Sistema non valido o senza ID', system);
      return;
    }

    // Check if we're deselecting the current system
    if (selectedSystem && selectedSystem._id === system._id) {
      setSelectedSystem(null);
      setSelectedSystemTypes([]);
      setFormData(prev => ({
        ...prev,
        sistema: null,
        tipoSistema: null,
        tipiSistema: []
      }));
      return;
    }
    
    // Select the new system
    setSelectedSystem(system);
    setSelectedSystemTypes([]);
    setFormData(prev => ({
      ...prev,
      sistema: system,
      tipoSistema: null,
      tipiSistema: []
    }));
  };

  const loadUseCases = async () => {
    try {
      const response = await apiService.get('/api/use-cases');
      return response.data;
    } catch (error) {
      console.error('Errore nel caricamento degli use cases:', error);
      toast.error('Errore nel caricamento degli use cases');
      return [];
    }
  };

  const fetchUseCases = async () => {
    const useCases = await loadUseCases();
    return useCases;
  };

  const analyzeWithAI = async (assistanceRequestUseCases) => {
    try {
      const response = await apiService.post('/api/assistance-requests/analyze', {
        text: segnalazione,
        useCases: assistanceRequestUseCases
      });
      return response.data;
    } catch (error) {
      console.error('Errore nell\'analisi AI:', error);
      toast.error('Errore nell\'analisi AI');
      return null;
    }
  };

  const handleAnalysisSystemSelect = (impianto) => {
    handleSystemSelect(impianto, false);
  };

  // Handle system type selection
  const handleSystemTypeSelect = (tipo) => {
    // Make sure we have a valid type with an ID
    if (!tipo || !tipo._id) {
      console.error('Tipo impianto non valido o senza ID', tipo);
      return;
    }

    // Selezione multipla: un clic aggiunge o toglie il servizio
    const alreadySelected = selectedSystemTypes.some(t => isSameSystemType(t, tipo));
    const next = alreadySelected
      ? selectedSystemTypes.filter(t => !isSameSystemType(t, tipo))
      : [...selectedSystemTypes, tipo];
    setSelectedSystemTypes(next);
    setFormData(prev => ({
      ...prev,
      tipoSistema: next[0] || null,
      tipiSistema: next
    }));
  };

  const handleUseCaseSelect = (useCase) => {
    if (formData.useCase && formData.useCase.id === useCase.id) {
      setFormData(prev => ({
        ...prev,
        useCase: null
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      useCase: {
        id: useCase.id,
        nome: useCase.nome,
        ...useCase
      }
    }));
  };

  const handleGenericRequestSelect = () => {
    setFormData(prev => ({
      ...prev,
      useCase: {
        id: 'generic',
        nome: 'Richiesta generica'
      }
    }));
  };

  const startElapsedTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const stopElapsedTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const analyzeText = () => {
    if (!segnalazione.trim()) {
      toast.error('Inserisci una descrizione del problema');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      segnalazione
    }));
    
    nextStep();
  };

  const handleTextChange = (e) => {
    setSegnalazione(e.target.value);
  };

  const handleMicClick = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];
        
        recorder.ondataavailable = (e) => {
          chunks.push(e.data);
        };
        
        recorder.onstop = async () => {
          const audioBlob = new Blob(chunks, { type: 'audio/wav' });
          const wavBlob = await convertToWav(audioBlob);
          
          try {
            const formData = new FormData();
            formData.append('audio', wavBlob, 'recording.wav');
            
            const response = await apiService.post('/api/assistance-requests/transcribe', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            
            setSegnalazione(prev => prev + ' ' + response.data.text);
          } catch (error) {
            console.error('Errore nella trascrizione:', error);
            toast.error('Errore nella trascrizione audio');
          }
        };
        
        setMediaRecorder(recorder);
        setAudioChunks(chunks);
        recorder.start();
        setIsRecording(true);
        setElapsedTime(0);
        startElapsedTimer();
      } catch (error) {
        console.error('Errore nell\'accesso al microfono:', error);
        toast.error('Errore nell\'accesso al microfono');
      }
    } else {
      mediaRecorder.stop();
      setIsRecording(false);
      stopElapsedTimer();
    }
  };

  // Handle use case selection
  const handleUseCaseToggle = (useCase) => {
    setSelectedUseCases(prev => {
      // Check if the use case is already selected
      const isSelected = prev.some(uc => uc._id === useCase._id);
      
      // If selected, remove it, otherwise add it
      return isSelected
        ? prev.filter(uc => uc._id !== useCase._id)
        : [...prev, useCase];
    });
  };
  
  // Update form data when selectedUseCases changes
  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      useCases: selectedUseCases
    }));
  }, [selectedUseCases, setFormData]);
  
  const handleNext = () => {
    if (!segnalazione.trim()) {
      toast.error('Inserisci una descrizione del problema');
      return;
    }
    
    if (!selectedSystem) {
      toast.error('Seleziona un impianto');
      return;
    }
    
    if (selectedSystemTypes.length === 0) {
      toast.error('Seleziona almeno un tipo di impianto');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      segnalazione
    }));
    
    nextStep();
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-6">Descrizione del Problema</h2>
      
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Descrivi il problema riscontrato <span style={{ color: 'red' }}>*</span>
        </label>
        
        <div className="relative">
          <textarea
            value={segnalazione}
            onChange={handleTextChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            rows="6"
            placeholder="Descrivi in dettaglio il problema riscontrato..."
          />
          
          <button
            onClick={handleMicClick}
            className={`absolute right-2 bottom-2 p-2 rounded-full ${
              isRecording ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            <i className={`fa-solid fa-${isRecording ? 'stop' : 'microphone'}`}></i>
          </button>
          
          {isRecording && (
            <div className="absolute right-12 bottom-2 text-sm text-gray-500">
              {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
      </div>
      
      {/* Lista impianti del cliente */}
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-4">Impianti del cliente <span style={{ color: 'red' }}>*</span></h3>
        
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : systems.length === 0 ? (
          <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
            <p>Nessun impianto trovato per questo cliente.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {systems.map(system => (
              <div 
                key={system._id}
                className={`p-4 border rounded-lg transition-colors ${selectedSystem && selectedSystem._id === system._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <div className="flex items-start">
                  <div 
                    className={`mt-1 w-5 h-5 rounded-full border-2 flex-shrink-0 cursor-pointer ${selectedSystem && selectedSystem._id === system._id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}
                    onClick={() => handleSystemSelect(system)}
                  ></div>
                  
                  <div className="ml-3 flex-grow">
                    <h4 className="font-medium text-lg cursor-pointer" onClick={() => handleSystemSelect(system)}>{system.title || system.nome}</h4>
                    <div className="mt-1 text-sm">
                      {system.customerAddressLabel && (
                        <p className="text-gray-600">
                          <i className="fa-solid fa-location-dot mr-1 text-gray-400"></i>
                          {system.customerAddressLabel}
                        </p>
                      )}
                      
                      {/* Informazioni di distanza e tempo */}
                      {system.geoData && (system.geoData.distanceKm || system.geoData.distanceTime) && (
                        <div className="mt-2 flex items-center space-x-3">
                          {system.geoData.distanceKm > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                              <i className="fa-solid fa-route mr-1 text-blue-500"></i>
                              {formatDistance(system.geoData.distanceKm)}
                            </span>
                          )}
                          {system.geoData.distanceTime > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                              <i className="fa-solid fa-clock mr-1 text-blue-500"></i>
                              {formatDurationMinutes(system.geoData.distanceTime)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Lista tipi di impianto */}
                    {selectedSystem && selectedSystem._id === system._id && system.typesList && system.typesList.length > 0 && (
                      <div className="mt-4 bg-white p-3 rounded-md border border-gray-100">
                        <p className="font-medium mb-2">Tipi di impianto: <span style={{ color: 'red' }}>*</span> <span className="text-xs font-normal text-gray-500">(puoi sceglierne più di uno)</span></p>
                        <div className="space-y-3">
                          {system.typesList.map(tipo => (
                            <div 
                              key={tipo._id}
                              className={`p-3 border rounded ${selectedSystemTypes.some(t => isSameSystemType(t, tipo)) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                            >
                              <div className="flex items-start">
                                <div 
                                  className={`mt-1 w-4 h-4 rounded border-2 flex-shrink-0 cursor-pointer flex items-center justify-center ${selectedSystemTypes.some(t => isSameSystemType(t, tipo)) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}
                                  onClick={() => handleSystemTypeSelect(tipo)}
                                >
                                  {selectedSystemTypes.some(t => isSameSystemType(t, tipo)) && (
                                    <i className="fa-solid fa-check text-white" style={{ fontSize: '0.6rem' }}></i>
                                  )}
                                </div>
                                
                                <div className="ml-3 w-full">
                                  <p className="font-medium cursor-pointer" onClick={() => handleSystemTypeSelect(tipo)}>{tipo.typeLabel}</p>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2 text-sm mt-2">
                                    {tipo.testedDate && (
                                      <p className="mb-1"><span className="text-gray-600">Data collaudo:</span> {formatDate(tipo.testedDate)}</p>
                                    )}
                                    
                                    {!tipo.expirationWarrantyDate && (
                                      <p className="mb-1"><span className="text-gray-600">Garanzia:</span> <span className="text-gray-500 italic">non indicata</span></p>
                                    )}
                                    {tipo.expirationWarrantyDate && (
                                      <div className="flex items-center mb-1">
                                        <span className="text-gray-600 mr-1">Garanzia:</span>
                                        <span className={new Date() > new Date(tipo.expirationWarrantyDate) ? 'text-red-500' : 'text-green-500 font-medium'}>
                                          {new Date() > new Date(tipo.expirationWarrantyDate) ? 
                                            'Fuori garanzia' : 
                                            'In garanzia'}
                                        </span>
                                        <span className="text-xs text-gray-500 ml-1">
                                          {new Date() > new Date(tipo.expirationWarrantyDate) ? 
                                            `(Scaduta il ${formatDate(tipo.expirationWarrantyDate)})` : 
                                            `(Scade il ${formatDate(tipo.expirationWarrantyDate)})`
                                          }
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="flex justify-between">
        <button
          style={{
            backgroundColor: colors.buttonSecondary || '#e2e8f0',
            color: colors.text || '#4a5568',
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            transition: 'background-color 0.3s'
          }}
          onClick={prevStep}
        >
          <i className="fa-solid fa-chevron-left mr-2"></i>
          Indietro
        </button>
        
        <button
          style={{
            backgroundColor: !segnalazione.trim() ? '#94a3b8' : colors.primary || '#3a5a40',
            color: '#ffffff',
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            transition: 'background-color 0.3s',
            opacity: !segnalazione.trim() ? 0.7 : 1
          }}
          onClick={handleNext}
          disabled={!segnalazione.trim()}
        >
          Avanti
          <i className="fa-solid fa-chevron-right ml-2"></i>
        </button>
      </div>
    </div>
  );
};

const ImmaginiStep = ({ formData, setFormData, nextStep, prevStep }) => {
  const colors = useColorPalette(); // Hook per i colori del tema
  const [images, setImages] = useState(formData.immagini || []);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error('Puoi caricare al massimo 5 immagini');
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setImages(prev => [...prev, ...newImages]);
  };

  const rimuoviImmagine = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleNext = () => {
    setFormData(prev => ({
      ...prev,
      immagini: images.map(img => ({
        file: img.file,
        preview: img.preview
      }))
    }));
    
    nextStep();
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-6">Carica Immagini</h2>
      
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Carica fino a 5 immagini (opzionale)
        </label>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
            id="image-upload"
          />
          
          <label
            htmlFor="image-upload"
            className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <i className="fa-solid fa-upload mr-2"></i>
            Seleziona immagini
          </label>
          
          <p className="mt-2 text-sm text-gray-500">
            Trascina le immagini qui o clicca per selezionarle
          </p>
        </div>
        
        {images.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={image.preview}
                  alt={`Immagine ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                
                <button
                  onClick={() => rimuoviImmagine(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="flex justify-between">
        <button
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors flex items-center"
          onClick={prevStep}
        >
          <i className="fa-solid fa-chevron-left mr-2"></i>
          Indietro
        </button>
        
        <button 
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors relative"
          onClick={handleNext}
        >
          {uploading ? (
            <>
              <span className="opacity-0">Avanti</span>
              <span className="absolute inset-0 flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            </>
          ) : (
            'Avanti'
          )}
          <i className="fa-solid fa-chevron-right ml-2"></i>
        </button>
      </div>
    </div>
  );
};

const TipoAssistenzaStep = ({ formData, setFormData, nextStep, prevStep }) => {
  const colors = useColorPalette(); // Hook per i colori del tema
  const [assistanceTypes, setAssistanceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(formData.tipoAssistenza || null);
  const accountData = useSelector(selectAccountData);
  const hasInitializedRef = useRef(false);

  // Function to select assistance type
  const handleAssistanceTypeSelect = (tipo) => {
    if (selectedType && selectedType._id === tipo._id) {
      setSelectedType(null);
      setFormData(prev => ({
        ...prev,
        tipoAssistenza: null
      }));
      return;
    }
    
    setSelectedType(tipo);
    setFormData(prev => ({
      ...prev,
      tipoAssistenza: {
        _id: tipo._id,
        nome: tipo.nome,
        ...tipo
      }
    }));
  };

  useEffect(() => {
    const fetchAssistanceTypes = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('installamelo-auth-token');
        const url = `${import.meta.env.VITE_API_BASE_URL}/assistances?accountId=${accountData._id}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Errore nel caricamento dei tipi di assistenza');
        }
        
        const data = await response.json();
        setAssistanceTypes(data);
        
        // Se non c'è un tipo selezionato e non abbiamo già inizializzato, seleziona quello con default=true
        if (!selectedType && !hasInitializedRef.current) {
          const defaultType = data.find(type => type.default === true);
          if (defaultType) {
            handleAssistanceTypeSelect(defaultType);
          }
          hasInitializedRef.current = true;
        }
      } catch (error) {
        console.error('Errore nel caricamento dei tipi di assistenza:', error);
        toast.error('Errore nel caricamento dei tipi di assistenza');
      } finally {
        setLoading(false);
      }
    };

    fetchAssistanceTypes();
  }, [accountData._id, selectedType === null]); // Solo quando l'account cambia o quando deselezionato



  const handleNext = () => {
    if (!selectedType) {
      toast.error('Seleziona un tipo di assistenza');
      return;
    }
    
    nextStep();
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-6">Tipo di Assistenza</h2>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sortAssistanceTypes(assistanceTypes).map((tipo) => (
            <div
              key={tipo._id}
              className={`p-4 border rounded-lg cursor-pointer transition-colors shadow-sm hover:shadow ${
                selectedType?._id === tipo._id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              style={{
                borderLeftWidth: '4px',
                borderLeftColor: tipo.color || '#3B82F6',
                backgroundColor: selectedType?._id === tipo._id ? 'rgba(59, 130, 246, 0.1)' : 'white'
              }}
              onClick={() => handleAssistanceTypeSelect(tipo)}
            >
              <div className="flex items-start mb-2">
                <div className={`mt-1 w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 ${
                  selectedType?._id === tipo._id
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}></div>
                <div>
                  <h3 className="font-medium text-lg">{tipo.title || tipo.nome}</h3>
                  {tipo.description && (
                    <p className="mt-1 text-sm text-gray-600">{tipo.description}</p>
                  )}
                </div>
              </div>
              
              <AssistanceTypeServices tipo={tipo} />
            </div>
          ))}
        </div>
      )}
      
      <div className="flex justify-between mt-8">
        <button
          className="font-medium py-2 px-4 rounded-md transition-colors flex items-center"
          style={{ 
            backgroundColor: colors.background || '#f1f1f1',
            color: colors.text || '#333',
            border: `1px solid ${colors.border || '#ddd'}`,
            opacity: 1
          }}
          onClick={prevStep}
        >
          <i className="fa-solid fa-chevron-left mr-2"></i>
          Indietro
        </button>
        
        <button
          className="font-medium py-2 px-4 rounded-md transition-colors flex items-center"
          style={{ 
            backgroundColor: colors.primary || '#3a5a40',
            color: '#ffffff',
            opacity: !selectedType ? 0.7 : 1
          }}
          onClick={handleNext}
          disabled={!selectedType}
        >
          Avanti
          <i className="fa-solid fa-chevron-right ml-2"></i>
        </button>
      </div>
    </div>
  );
};

const PagamentoStep = ({ formData, setFormData, nextStep, prevStep, confirmSubmit, isSubmitting, isEditMode }) => {
  // Stato per la modale di conferma
  const colors = useColorPalette(); // Hook per i colori del tema
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const accountData = useSelector(selectAccountData);
  
  // Assicuriamoci che formData sia definito e inizializziamo pagamento con valori di default sicuri
  const safeFormData = formData || {};
  const safePagamento = safeFormData.pagamento || {};
  
  const [pagamento, setPagamento] = useState({
    metodo: safePagamento.metodo || '',
    fasciaOraria: safePagamento.fasciaOraria || '',
    teleassistenza: safePagamento.teleassistenza || false
  });
  
  const [accountSettings, setAccountSettings] = useState(null);
  const [loadingAccountSettings, setLoadingAccountSettings] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(safePagamento.fasciaOraria || 'giornata');
  const [teleassistenza, setTeleassistenza] = useState(safePagamento.teleassistenza || false);
  
  // Determina se il cliente è business (ha la proprietà business = true)
  const isBusinessClient = formData?.cliente?.business === true;
  
  // Carica le impostazioni dell'account
  useEffect(() => {
    if (accountData && accountData.settings) {
      // Log per debug
      console.log('accountData.settings:', accountData.settings);
      console.log('accountData.settings.paymentTypes:', accountData.settings.paymentTypes);
      
      // Usa i dati dell'account già caricati nel redux store
      // Non fornire valori di fallback per evitare di mostrare dati fake
      const paymentMethods = accountData.settings.paymentTypes || [];
      console.log('Payment methods utilizzati:', paymentMethods);
      
      setAccountSettings({
        paymentMethods: paymentMethods
      });
      
      // Gestione della selezione del metodo di pagamento per tutti i clienti
      // Resetta prima la selezione corrente per assicurarci che non ci siano selezioni multiple
      setSelectedPaymentMethod(null);
      
      // Preferenza 1: Se c'è già un metodo salvato in formData.pagamento, usa quello
      if (safePagamento.metodo) {
        console.log('Utilizzo metodo già selezionato:', safePagamento.metodo);
        setSelectedPaymentMethod(safePagamento.metodo);
      } 
      // Preferenza 2: Altrimenti trova e usa il metodo con default=true
      else {
        const defaultPaymentMethod = paymentMethods.find(method => method.default === true);
        
        if (defaultPaymentMethod) {
          console.log('Seleziono metodo di pagamento default:', defaultPaymentMethod);
          setSelectedPaymentMethod(defaultPaymentMethod._id);
          // Aggiorna anche il formData per mantenere lo stato coerente
          setFormData(prev => ({
            ...prev,
            pagamento: {
              ...prev.pagamento,
              metodo: defaultPaymentMethod._id
            }
          }));
        } 
        // Preferenza 3: Se non c'è un metodo con default=true, usa il primo della lista
        else if (paymentMethods.length > 0) {
          console.log('Nessun metodo default, uso il primo disponibile:', paymentMethods[0]);
          setSelectedPaymentMethod(paymentMethods[0]._id);
          setFormData(prev => ({
            ...prev,
            pagamento: {
              ...prev.pagamento,
              metodo: paymentMethods[0]._id
            }
          }));
        }
      }
      
      // Imposta sempre la fascia oraria a 'giornata' se non è specificata
      if (!safePagamento.fasciaOraria) {
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            fasciaOraria: 'giornata'
          }
        }));
      }
      
      setLoadingAccountSettings(false);
      
      // Se è attiva la teleassistenza, assicurati che sia selezionato solo un metodo di pagamento con carta
      if (safePagamento.teleassistenza && isEditMode) {
        console.log('Richiesta caricata in modalità edit con teleassistenza attiva, applico restrizioni sui metodi di pagamento');
        // Verifica se il metodo di pagamento attuale è compatibile con la teleassistenza
        if (safePagamento.metodo) {
          const currentMethod = paymentMethods.find(m => m._id === safePagamento.metodo);
          if (currentMethod) {
            const methodName = (currentMethod.name || currentMethod.nome || currentMethod.title || currentMethod.label || '').toLowerCase();
            const isCardPayment = methodName.includes('carta') || 
                               methodName.includes('credit') || 
                               methodName.includes('bancomat') || 
                               methodName.includes('pos');
            
            // Se il metodo attuale non è un pagamento con carta, trova un metodo con carta
            if (!isCardPayment) {
              console.log('Metodo di pagamento attuale non compatibile con teleassistenza, cerco un metodo con carta');
              const cardPaymentMethod = paymentMethods.find(method => {
                const name = (method.name || method.nome || method.title || method.label || '').toLowerCase();
                return name.includes('carta') || name.includes('credit') || name.includes('bancomat') || name.includes('pos');
              });
              
              if (cardPaymentMethod) {
                console.log('Trovato metodo di pagamento con carta per teleassistenza:', cardPaymentMethod);
                setSelectedPaymentMethod(cardPaymentMethod._id);
                setFormData(prev => ({
                  ...prev,
                  pagamento: {
                    ...prev.pagamento,
                    metodo: cardPaymentMethod._id
                  }
                }));
              }
            }
          }
        }
      }
    } else {
      // Imposta valori predefiniti se non ci sono impostazioni o non c'è accountData
      const defaultPaymentMethods = [
        { id: 'contanti', nome: 'Contanti', default: true },
        { id: 'carta', nome: 'Carta di Credito/Debito' },
        { id: 'bonifico', nome: 'Bonifico Bancario' }
      ];
      
      setAccountSettings({
        paymentMethods: defaultPaymentMethods
      });
      
      // Se il cliente è privato, gestisci la selezione anche nel caso in cui non ci sono impostazioni dell'account
      if (!isBusinessClient && !selectedPaymentMethod && defaultPaymentMethods.length > 0) {
        console.log('Seleziono metodo di pagamento dai valori predefiniti');
        // Cerca quello con default=true o prendi il primo
        const defaultMethod = defaultPaymentMethods.find(method => method.default === true) || defaultPaymentMethods[0];
        console.log('Metodo selezionato:', defaultMethod);
        setSelectedPaymentMethod(defaultMethod._id);
        // Aggiorna anche il formData
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            metodo: defaultMethod._id
          }
        }));
      }
      
      // Imposta sempre la fascia oraria a 'giornata' se non è specificata
      if (!safePagamento.fasciaOraria) {
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            fasciaOraria: 'giornata'
          }
        }));
      }
      
      setLoadingAccountSettings(false);
    }
  }, [accountData]);

  const formattaData = (dataString) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('it-IT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const mappaFasciaOraria = (valore) => {
    switch (valore) {
      case 'mattina':
        return 'Mattina (9:00 - 13:00)';
      case 'pomeriggio':
        return 'Pomeriggio (14:00 - 18:00)';
      case 'giornata':
        return 'Giornata intera (9:00 - 18:00)';
      default:
        return valore;
    }
  };

  const formattaTipoAssistenza = (tipo) => {
    if (!tipo) return '';
    return tipo.nome;
  };

  const handlePaymentMethodSelect = (method) => {
    // Prima resetta tutte le selezioni
    setSelectedPaymentMethod(null);
    
    // Poi imposta solo il metodo selezionato
    setSelectedPaymentMethod(method);
    setFormData(prev => ({
      ...prev,
      pagamento: {
        ...prev.pagamento,
        metodo: method
      }
    }));
  };

  const handleTimeSlotSelect = (timeSlot) => {
    if (selectedTimeSlot === timeSlot) {
      setSelectedTimeSlot(null);
      setFormData(prev => ({
        ...prev,
        pagamento: {
          ...prev.pagamento,
          fasciaOraria: null
        }
      }));
      return;
    }
    
    setSelectedTimeSlot(timeSlot);
    setFormData(prev => ({
      ...prev,
      pagamento: {
        ...prev.pagamento,
        fasciaOraria: timeSlot
      }
    }));
  };

  const handleTeleassistenzaChange = (checked) => {
    setTeleassistenza(checked);
    setPagamento(prev => ({
      ...prev,
      teleassistenza: checked
    }));
    
    // Se la teleassistenza è attivata, seleziona automaticamente il metodo di pagamento carta/bancomat
    if (checked && accountSettings?.paymentMethods) {
      // Cerca il metodo di pagamento carta/bancomat
      const cardPaymentMethod = accountSettings.paymentMethods.find(method => {
        const methodName = (method.name || method.nome || method.title || method.label || '').toLowerCase();
        return methodName.includes('carta') || 
               methodName.includes('credit') || 
               methodName.includes('bancomat') || 
               methodName.includes('pos');
      });
      
      // Se trovato, seleziona automaticamente questo metodo
      if (cardPaymentMethod) {
        setSelectedPaymentMethod(cardPaymentMethod._id);
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            teleassistenza: checked,
            metodo: cardPaymentMethod._id
          }
        }));
      } else {
        // Se non trovato, aggiorna comunque il formData con il flag teleassistenza
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            teleassistenza: checked
          }
        }));
      }
    } else {
      // Se disattivata, aggiorna solo il flag teleassistenza
      setFormData(prev => ({
        ...prev,
        pagamento: {
          ...prev.pagamento,
          teleassistenza: checked
        }
      }));
    }
  };

  const handleCardPaymentSelection = (needCardPayment) => {
    if (needCardPayment && accountSettings?.paymentMethods) {
      // Cerca il metodo di pagamento carta/bancomat
      const cardPaymentMethod = accountSettings.paymentMethods.find(method => {
        const methodName = (method.name || method.nome || method.title || method.label || '').toLowerCase();
        return methodName.includes('carta') || 
               methodName.includes('credit') || 
               methodName.includes('bancomat') || 
               methodName.includes('pos');
      });
      
      if (cardPaymentMethod) {
        setSelectedPaymentMethod(cardPaymentMethod._id);
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            teleassistenza: needCardPayment,
            metodo: cardPaymentMethod._id
          }
        }));
        return true;
      }
    }
    return false;
  };

  const handleNext = () => {
    // Il metodo di pagamento è obbligatorio per tutti i clienti
    if (!selectedPaymentMethod) {
      toast.error('Seleziona un metodo di pagamento');
      return;
    }
    
    // Assicuriamoci che la fascia oraria sia impostata, default a 'giornata'
    if (!selectedTimeSlot) {
      setSelectedTimeSlot('giornata');
      setFormData(prev => ({
        ...prev,
        pagamento: {
          ...prev.pagamento,
          fasciaOraria: 'giornata'
        }
      }));
    }
    
    // Mostra la modale di conferma invece di procedere direttamente
    setShowConfirmModal(true);
  };
  
  const handleConfirm = () => {
    // Chiudi la modale e procedi con la sottomissione
    setShowConfirmModal(false);
    // Usa la funzione confirmSubmit passata come prop
    confirmSubmit && typeof confirmSubmit === 'function' ? confirmSubmit() : null;
  };
  
  const handleCancel = () => {
    // Chiudi solo la modale
    setShowConfirmModal(false);
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-6">Pagamento e Appuntamento</h2>
      
      {loadingAccountSettings ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : accountSettings ? (
        <>
          {/* Opzione per teleassistenza */}
          <div className="mb-8 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="teleassistenza"
                checked={teleassistenza}
                onChange={(e) => handleTeleassistenzaChange(e.target.checked)}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="teleassistenza" className="ml-3 cursor-pointer">
                <span className="font-medium text-gray-800">Richiedi Teleassistenza</span>
                <p className="text-sm text-gray-600 mt-1">Seleziona questa opzione se desideri richiedere supporto tecnico da remoto</p>
              </label>
            </div>
            {/* Disclaimer pagamento anticipato per teleassistenza */}
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <i className="fa-solid fa-triangle-exclamation text-amber-600 mt-0.5"></i>
                <p className="text-amber-800 text-sm ml-3">
                  <strong>Nota:</strong> La modalità teleassistenza prevede il pagamento anticipato. 
                  Il cliente verrà contattato dall'amministrazione di Securfox per completare il pagamento anticipato.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-medium mb-4">Metodo di Pagamento</h3>
            
            {/* Selezione dei metodi di pagamento per tutti i clienti */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {accountSettings?.paymentMethods?.map((method) => {
                // Determina se è un metodo di pagamento con carta
                const methodName = (method.name || method.nome || method.title || method.label || '').toLowerCase();
                const isCardPayment = methodName.includes('carta') || 
                                     methodName.includes('credit') || 
                                     methodName.includes('bancomat') || 
                                     methodName.includes('pos');
                
                // Se è attiva la teleassistenza, mostra solo metodi pagamento con carta
                // Se non è attiva la teleassistenza, mostra tutti i metodi
                const shouldShow = !teleassistenza || (teleassistenza && isCardPayment);
                
                // Se il metodo non deve essere mostrato, salta questo elemento
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={method._id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedPaymentMethod === method._id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    } ${
                      teleassistenza && isCardPayment ? 'ring-2 ring-blue-300' : ''
                    }`}
                    onClick={() => handlePaymentMethodSelect(method._id)}
                  >
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                        selectedPaymentMethod === method._id
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}></div>
                      <h4 className="font-medium">{method.name || method.nome || method.title || method.label}</h4>
                      {teleassistenza && isCardPayment && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          Obbligatorio per teleassistenza
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-4">Fascia Oraria</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['mattina', 'pomeriggio', 'giornata'].map((timeSlot) => (
                <div
                  key={timeSlot}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedTimeSlot === timeSlot
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => handleTimeSlotSelect(timeSlot)}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                      selectedTimeSlot === timeSlot
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}></div>
                    <h4 className="font-medium">{mappaFasciaOraria(timeSlot)}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Rimossa la sezione Data come richiesto */}
        </>
      ) : (
        <div className="p-4 text-center">
          <p>Nessuna impostazione di pagamento disponibile</p>
        </div>
      )}
      
      <div className="flex justify-between">
        <button
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors flex items-center"
          onClick={prevStep}
        >
          <i className="fa-solid fa-chevron-left mr-2"></i>
          Indietro
        </button>
        
        <button
          className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center"
          onClick={handleNext}
          disabled={!selectedPaymentMethod || !selectedTimeSlot} // Metodo di pagamento e fascia oraria sono obbligatori per tutti
        >
          {isEditMode ? 'Salva modifiche' : 'Crea nuova assistenza'}
          <i className={`fa-solid ${isEditMode ? 'fa-save' : 'fa-plus-circle'} ml-2`}></i>
        </button>
      </div>
      
      {/* Modale di conferma */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">{isEditMode ? 'Modifica richiesta di assistenza' : 'Conferma richiesta di assistenza'}</h3>
            <p className="mb-6">{isEditMode ? 'Sei sicuro di voler aggiornare questa richiesta di assistenza?' : 'Sei sicuro di voler creare questa richiesta di assistenza?'}</p>
            
            <div className="flex justify-between space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors relative flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <span className="opacity-0">Crea nuova assistenza</span>
                    <span className="absolute inset-0 flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  </>
                ) : (
                  <>
                    {isEditMode ? (
                      <>
                        <i className="fa-solid fa-save mr-2"></i>
                        Salva modifiche
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-plus-circle mr-2"></i>
                        Crea nuova assistenza
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Il componente StepIcon è stato spostato nel componente WizardHeader

const AssistancePreview = ({ formData, currentStep, submitForm }) => {
  const colors = useColorPalette(); // Hook per i colori del tema
  // Accesso ai dati dell'account tramite Redux
  const accountData = useSelector(selectAccountData);
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const mappaFasciaOraria = (valore) => {
    switch (valore) {
      case 'mattina':
        return 'Mattina (9:00 - 13:00)';
      case 'pomeriggio':
        return 'Pomeriggio (14:00 - 18:00)';
      case 'giornata':
        return 'Giornata intera (9:00 - 18:00)';
      default:
        return valore;
    }
  };

  const formattaTipoAssistenza = (tipo) => {
    if (!tipo) return '';
    return tipo.nome;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-3">
      <div className="space-y-4">


        {/* Box per il codice dell'assistenza (visibile solo in modalità modifica) */}
        {formData.isEditMode && formData.code && (
          <div className={`mb-2 p-2 rounded-md`} style={{ 
            backgroundColor: `${colors.primary}15`,
            border: `1px solid ${colors.primary}30`
          }}>
            <h4 className="text-md font-semibold mb-2 flex items-center">
              <i className="fa-solid fa-hashtag mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
              Codice Assistenza
            </h4>
            <p className="font-medium">{formData.code}</p>
          </div>
        )}

        <div className={`mb-2 p-2 rounded-md`} style={{ 
          backgroundColor: `${colors.primary}15`,
          border: `1px solid ${colors.primary}30`
        }}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-user mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
            Cliente
          </h4>
          {formData.cliente ? (
            <div>
              <p className="font-medium">{formData.cliente.nome}</p>
              {formData.cliente.vatNumber && <p className="text-sm text-gray-600">P.IVA: {formData.cliente.vatNumber}</p>}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Seleziona un cliente</p>
          )}
        </div>

        <div className={`mb-2 p-2 rounded-md`} style={{ 
          backgroundColor: `${colors.primary}15`,
          border: `1px solid ${colors.primary}30`
        }}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-cogs mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
            Sistema
          </h4>
          {formData.sistema ? (
            <div>
              <p className="font-medium">{formData.sistema.title || formData.sistema.nome}</p>
              {formData.sistema.customerAddressLabel && (
                <p className="text-sm text-gray-600">
                  <i className="fa-solid fa-location-dot mr-1 text-gray-400"></i>
                  {formData.sistema.customerAddressLabel}
                </p>
              )}
              {getSelectedSystemTypes(formData).map(tipo => (
                <div key={tipo._id || tipo.typeId} className="mt-2 pt-2 border-t border-blue-100">
                  <p className="font-medium">Tipo di sistema: {tipo.typeLabel}</p>
                  <div className="grid grid-cols-1 gap-x-2 text-xs mt-1">
                    {tipo.testedDate && (
                      <p className="mb-1">Data collaudo: {formatDate(tipo.testedDate)}</p>
                    )}
                    {!tipo.expirationWarrantyDate && (
                      <p className="mb-1">Garanzia: <span className="text-gray-500 italic">non indicata</span></p>
                    )}
                    {tipo.expirationWarrantyDate && (
                      <div className="flex items-center mb-1">
                        <span className="mr-1">Garanzia:</span>
                        <span className={new Date() > new Date(tipo.expirationWarrantyDate) ? 'text-red-500' : 'text-green-500 font-medium'}>
                          {new Date() > new Date(tipo.expirationWarrantyDate) ? 
                            'Fuori garanzia' : 
                            'In garanzia'}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          {new Date() > new Date(tipo.expirationWarrantyDate) ? 
                            `(Scaduta il ${formatDate(tipo.expirationWarrantyDate)})` : 
                            `(Scade il ${formatDate(tipo.expirationWarrantyDate)})`
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Seleziona un sistema</p>
          )}
        </div>

        <div className={`mb-2 p-2 rounded-md`} style={{ 
          backgroundColor: `${colors.primary}15`,
          border: `1px solid ${colors.primary}30`
        }}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-comment mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
            Descrizione
          </h4>
          {formData.segnalazione ? (
            <p className="text-sm">{formData.segnalazione.length > 100 ? formData.segnalazione.substring(0, 100) + '...' : formData.segnalazione}</p>
          ) : (
            <p className="text-sm text-gray-500 italic">Aggiungi una descrizione del problema</p>
          )}
        </div>
        
        {/* Use Cases Preview */}
        <div className={`mb-2 p-2 rounded-md`} style={{ 
          backgroundColor: `${colors.primary}15`,
          border: `1px solid ${colors.primary}30`
        }}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-tag mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
            Categorie
          </h4>
          {formData.useCases && formData.useCases.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {formData.useCases.map(useCase => (
                <span key={useCase._id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {useCase.label}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Nessuna categoria selezionata</p>
          )}
        </div>

        <div className={`mb-2 p-2 rounded-md`} style={{ 
          backgroundColor: `${colors.primary}15`,
          border: `1px solid ${colors.primary}30`
        }}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-image mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
            Immagini
          </h4>
          {formData.immagini && formData.immagini.length > 0 ? (
            <div>
              <p className="text-sm">{formData.immagini.length} {formData.immagini.length === 1 ? 'immagine caricata' : 'immagini caricate'}</p>
              {formData.immagini.length > 0 && (
                <div className="flex mt-2 flex-wrap gap-2">
                  {formData.immagini.slice(0, 3).map((img, idx) => (
                    <div key={idx} className="w-16 h-16 relative rounded overflow-hidden">
                      <img src={img.preview} alt={`Anteprima ${idx+1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {formData.immagini.length > 3 && (
                    <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-gray-600">
                      +{formData.immagini.length - 3}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Nessuna immagine caricata (opzionale)</p>
          )}
        </div>

        <div 
          className={`mb-2 p-2 rounded-md relative overflow-hidden`}
          style={{ 
            backgroundColor: `${colors.primary}15`,
            border: `1px solid ${colors.primary}30`,
            borderLeftWidth: formData.tipoAssistenza && formData.tipoAssistenza.color ? '4px' : '1px',
            borderLeftColor: formData.tipoAssistenza && formData.tipoAssistenza.color ? formData.tipoAssistenza.color : `${colors.primary}30`
          }}
        >
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-wrench mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
            Tipo di Assistenza
          </h4>
          {formData.tipoAssistenza ? (
            <div>
              <p className="font-medium">{formData.tipoAssistenza.title || formData.tipoAssistenza.nome}</p>
              {formData.tipoAssistenza.description && (
                <p className="text-sm text-gray-600 mb-2">{formData.tipoAssistenza.description}</p>
              )}
              
              {/* Servizi inclusi: stesso componente della card */}
              <AssistanceTypeServices tipo={formData.tipoAssistenza} compact />
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Seleziona un tipo di assistenza</p>
          )}
        </div>

        <div className={`mb-2 p-2 rounded-md`} style={{ 
          backgroundColor: `${colors.primary}15`,
          border: `1px solid ${colors.primary}30`
        }}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-credit-card mr-2" style={{ color: colors.icons || '#3a5a40' }}></i>
            Pagamento e Appuntamento
          </h4>
          {formData.pagamento && formData.pagamento.metodo ? (
            <div>
              <p className="text-sm"><strong>Metodo:</strong> {
                // Recupera il nome del metodo di pagamento dall'ID
                (() => {
                  if (!formData.pagamento.metodo) return 'Non specificato';
                  
                  // Cerca nelle impostazioni dell'account
                  const paymentMethod = accountData?.settings?.paymentTypes?.find(m => m._id === formData.pagamento.metodo);
                  if (paymentMethod) return paymentMethod.name || paymentMethod.nome || paymentMethod.title || paymentMethod.label;
                  
                  // Se non trovato, mostra almeno l'ID
                  return formData.pagamento.metodo;
                })()
              }</p>
              {formData.pagamento.data && (
                <p className="text-sm"><strong>Data:</strong> {formatDate(formData.pagamento.data)}</p>
              )}
              {formData.pagamento.fasciaOraria && (
                <p className="text-sm"><strong>Fascia oraria:</strong> {mappaFasciaOraria(formData.pagamento.fasciaOraria)}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Dati di pagamento e appuntamento non inseriti</p>
          )}
        </div>

        {/* Mostra il pulsante solo nell'ultimo step (Pagamento) quando tutti i dati sono completi */}
        {currentStep === 4 && formData.cliente && formData.segnalazione && formData.tipoAssistenza && 
         formData.pagamento && formData.pagamento.metodo && formData.pagamento.data && formData.pagamento.fasciaOraria && (
          <button
            className="w-full py-3 px-4 font-medium rounded-md transition-colors flex items-center justify-center mt-4"
            style={{ 
              backgroundColor: colors.buttonSuccess || '#2a9d8f',
              color: '#ffffff'
            }}
            onClick={submitForm}
          >
            <i className="fa-solid fa-check-circle mr-2"></i>
            Conferma e Invia
          </button>
        )}
      </div>
    </div>
  );
};

const AddEditAssistanceRequestNoAI = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const colors = useColorPalette(); // Hook per i colori del tema
  const accountData = useSelector(selectAccountData);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false); // Stato per gestire il caricamento
  const [isEditMode, setIsEditMode] = useState(false); // Stato per la modalità di modifica
  const [formData, setFormData] = useState({
    cliente: null,
    segnalazione: '',
    immagini: [],
    tipoAssistenza: null,
    pagamento: {
      metodo: '',
      fasciaOraria: '',
      data: ''
    }
  });

  const steps = [
    { title: 'Cliente', icon: faUser },
    { title: 'Segnalazione', icon: faComment },
    { title: 'Immagini', icon: faImage },
    { title: 'Tipo Assistenza', icon: faWrench },
    { title: 'Pagamento', icon: faCreditCard }
  ];
  
  // Effetto per caricare i dati della richiesta se siamo in modalità modifica
  useEffect(() => {
    const loadExistingRequest = async () => {
      // Controlla se siamo in modalità modifica (c'è un id nell'URL o nel location state)
      if (id || (location.state && location.state.requestData)) {
        setIsEditMode(true);
        
        // Utilizza i dati dal location state se disponibili
        if (location.state && location.state.requestData) {
          const requestData = location.state.requestData;
          console.log('Caricamento dati dalla request esistente:', requestData);
          
          // Mappa i dati della richiesta nel formato utilizzato da formData
          setFormData({
            code: requestData.code, // Aggiungiamo il codice dell'assistenza
            cliente: {
              id: requestData.customerId,
              nome: requestData.customerLabel,
              business: requestData.customerBusiness
            },
            sistema: {
              _id: requestData.systemId,
              title: requestData.systemLabel,
              nome: requestData.systemLabel,
              customerAddressId: requestData.systemAddressId,
              customerAddressLabel: requestData.systemAddressLabel
            },
            tipoSistema: {
              _id: requestData.systemTypeId,
              typeLabel: requestData.systemTypeLabel,
              expirationWarrantyDate: requestData.systemTypeExpirationWarrantyDate
            },
            tipiSistema: systemTypesFromRequest(requestData),
            segnalazione: requestData.customerRequest || requestData.description || '',
            analisiRichiesta: requestData.analisiRichiesta,
            analisiInfo: requestData.analisiInfo,
            useCases: requestData.useCases || [],
            immagini: Array.isArray(requestData.uploads) 
              ? requestData.uploads.map((url, index) => ({
                  id: `upload-${index}`,
                  file: null, // Non abbiamo il file originale
                  preview: url,
                  url: url
                }))
              : [],
            tipoAssistenza: {
              _id: requestData.assistanceTypeId,
              title: requestData.assistanceTypeLabel,
              nome: requestData.assistanceTypeLabel,
              description: requestData.assistanceTypeDescription,
              priority: {
                _id: requestData.assistanceTypePriorityId,
                priorityLabel: requestData.assistanceTypePriorityLabel,
                priorityValue: requestData.assistanceTypePriorityValue
              }
            },
            pagamento: {
              metodo: requestData.paymentTypeId,
              fasciaOraria: requestData.appointmentTimeSlot || requestData.fasciaOraria || 'giornata',
              data: requestData.plannedDate,
              teleassistenza: requestData.teleassistenza || false
            },
            isEditMode: true
          });
          
          return;
        }
        
        // Se non ci sono dati nel location state ma c'è un ID, carica i dati dall'API
        if (id) {
          try {
            const token = localStorage.getItem('installamelo-auth-token');
            if (!token) {
              toast.error('Errore: Sessione scaduta. Effettua nuovamente l\'accesso.');
              return;
            }
            
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/assistanceRequests/${id}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'auth-token': token
              }
            });
            
            if (!response.ok) {
              throw new Error('Errore nel caricamento della richiesta');
            }
            
            const requestData = await response.json();
            console.log('Richiesta caricata da API:', requestData);
            
            // Mappatura analoga a quella sopra
            setFormData({
              code: requestData.code, // Aggiungiamo il codice dell'assistenza
              cliente: {
                id: requestData.customerId,
                nome: requestData.customerLabel,
                business: requestData.customerBusiness
              },
              sistema: {
                _id: requestData.systemId,
                title: requestData.systemLabel,
                nome: requestData.systemLabel,
                customerAddressId: requestData.systemAddressId,
                customerAddressLabel: requestData.systemAddressLabel
              },
              tipoSistema: {
                _id: requestData.systemTypeId,
                typeLabel: requestData.systemTypeLabel,
                expirationWarrantyDate: requestData.systemTypeExpirationWarrantyDate
              },
              tipiSistema: systemTypesFromRequest(requestData),
              segnalazione: requestData.customerRequest || requestData.description || '',
              analisiRichiesta: requestData.analisiRichiesta,
              analisiInfo: requestData.analisiInfo,
              useCases: requestData.useCases || [],
              immagini: Array.isArray(requestData.uploads) 
                ? requestData.uploads.map((url, index) => ({
                    id: `upload-${index}`,
                    file: null,
                    preview: url,
                    url: url
                  }))
                : [],
              tipoAssistenza: {
                _id: requestData.assistanceTypeId,
                title: requestData.assistanceTypeLabel,
                nome: requestData.assistanceTypeLabel,
                description: requestData.assistanceTypeDescription,
                priority: {
                  _id: requestData.assistanceTypePriorityId,
                  priorityLabel: requestData.assistanceTypePriorityLabel,
                  priorityValue: requestData.assistanceTypePriorityValue
                }
              },
              pagamento: {
                metodo: requestData.paymentTypeId,
                fasciaOraria: requestData.appointmentTimeSlot || requestData.fasciaOraria || 'giornata',
                data: requestData.plannedDate
              },
              isEditMode: true
            });
          } catch (error) {
            console.error('Errore durante il caricamento della richiesta:', error);
            toast.error('Impossibile caricare i dati della richiesta');
            navigate('/assistanceRequestsNoAI');
          }
        }
      }
    };
    
    loadExistingRequest();
  }, [id, location.state, navigate]);

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleFormDataUpdate = (newData) => {
    setFormData(prev => ({
      ...prev,
      ...newData
    }));
  };

  // Funzione wrapper senza parametri per chiamate non legate ad eventi
  const submitWithoutEvent = async () => {
    await handleFinalSubmit();
  };
  
  const handleFinalSubmit = async (e) => {
    // Previeni il comportamento predefinito solo se e è definito (chiamato da un evento DOM)
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    // Controlla se è in corso una sottomissione e interrompi se è vero
    if (isSubmitting) return;
    
    // Controlla se lo stato 'creata' non è stato trovato - interrompe la creazione
    if (window.statusCreatoNotFound) {
      toast.error('Impossibile creare l\'assistenza: stato "creata" non trovato nelle impostazioni del sistema.');
      return;
    }
    
    setIsSubmitting(true); // Attiva lo stato di caricamento
    
    try {
      // Determina se siamo in modalità creazione o modifica
      const isCreatingNew = !isEditMode;
      // Utilizza l'ID dell'account (già recuperato all'inizio del componente) e il token di autenticazione
      const token = localStorage.getItem('installamelo-auth-token');
      
      if (!token) {
        toast.error('Errore: Sessione scaduta. Effettua nuovamente l\'accesso.');
        return;
      }
      
      // Funzione per caricare una singola immagine sul server
      const uploadImage = async (image) => {
        // Se l'immagine è già un URL o una stringa, la restituiamo direttamente
        if (typeof image === 'string') {
          console.log('Immagine già in formato URL:', image);
          return image;
        }
        
        // Determiniamo se l'oggetto è un File diretto o un oggetto che contiene un File
        let fileObject;
        let titleText;
        
        if (image instanceof File) {
          // È direttamente un oggetto File
          console.log('Immagine è un oggetto File diretto:', image.name);
          fileObject = image;
          titleText = image.name;
        } else if (image && image.file instanceof File) {
          // È un oggetto che contiene un File nella proprietà 'file'
          console.log('Immagine è un oggetto con File:', image.file.name);
          fileObject = image.file;
          titleText = image.title || image.file.name;
        } else {
          console.warn('Formato immagine non valido, né File né oggetto con file:', image);
          return null;
        }
        
        // Verificare il tipo di file
        const fileName = fileObject.name;
        const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
        
        // Controlla che sia un'immagine supportata
        if (!(ext === "gif" || ext === "png" || ext === "jpeg" || ext === "jpg" || ext === "pdf")) {
          console.warn(`Tipo di file non consentito: ${ext}`);
          toast.error("Tipo di File non consentito!");
          return null;
        }
        
        try {
          // Prepara i dati per il caricamento
          const data = new FormData();
          data.append('file', fileObject, fileName);
          data.append('uploadName', fileName);
          data.append('uploadTitle', titleText);
          data.append('path', `assistanceRequests/${accountData._id}/${formData.cliente.id}`);
          
          console.log(`Tentativo di caricamento dell'immagine ${fileName}...`);
          
          // Chiamata per caricare l'immagine
          const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/files/upload`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'auth-token': token
            },
            body: data
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Errore nell'upload dell'immagine: ${errorText}`);
          }
          
          const result = await response.json();
          console.log('Immagine caricata con successo:', result);
          return result.url || result.fileUrl || result; // Restituisce l'URL dell'immagine caricata
        } catch (error) {
          console.error(`Errore durante il caricamento dell'immagine:`, error);
          throw error;
        }
      };
      
      // 1. Prima carichiamo tutte le immagini e otteniamo i loro URL
      let uploadedImageUrls = [];
      
      if (Array.isArray(formData.immagini) && formData.immagini.length > 0) {
        console.log(`Caricamento di ${formData.immagini.length} immagini...`, formData.immagini);
        
        // Mostriamo un messaggio di caricamento
        toast.info("Caricamento delle immagini in corso...", { autoClose: false, toastId: 'uploading-images' });
        
        // Carichiamo le immagini una per una
        for (const img of formData.immagini) {
          try {
            const url = await uploadImage(img);
            if (url) {
              uploadedImageUrls.push(url);
              console.log(`Immagine caricata: ${url}`);
            }
          } catch (imgError) {
            console.error(`Errore per una singola immagine:`, imgError);
            // Continuiamo con le altre immagini anche se una fallisce
          }
        }
        
        // Aggiorniamo l'utente sui risultati
        toast.dismiss('uploading-images');
        if (uploadedImageUrls.length > 0) {
          toast.success(`${uploadedImageUrls.length} immagini caricate con successo`);
          console.log('Immagini caricate:', uploadedImageUrls);
        } else {
          toast.warning("Nessuna immagine caricata con successo");
        }
      }
      
      // In modalità modifica, preserviamo alcuni campi come il codice e lo stato attuale
      const assistanceRequest = {
        // Dati account e cliente
        accountId: accountData._id,
        customerId: formData.cliente.id,
        customerLabel: formData.cliente.nome,
        
        // Dati sistema
        systemId: formData.sistema?._id,
        systemLabel: formData.sistema?.title || formData.sistema?.nome,
        systemAddressId: formData.sistema?.customerAddressId,
        systemAddressLabel: formData.sistema?.customerAddressLabel,
        
        // Dati tipo sistema
        systemTypeId: formData.tipoSistema?._id,
        systemTypeLabel: formData.tipoSistema?.typeLabel,
        systemTypeWarranty: formData.tipoSistema?.expirationWarrantyDate ? 
          new Date() <= new Date(formData.tipoSistema.expirationWarrantyDate) : false,
        systemTypeExpirationWarrantyDate: formData.tipoSistema?.expirationWarrantyDate,
        // Tutti i servizi scelti: il server li riporta all'anagrafica servizi e calcola la garanzia
        systemTypes: getSelectedSystemTypes(formData).map(t => ({
          typeId: t.typeId || t._id,
          typeLabel: t.typeLabel
        })),
        
        // Dati richiesta
        customerRequest: formData.segnalazione,
        description: formData.segnalazione, // Aggiungo il campo description con la segnalazione
        useCases: formData.useCases || [],
        
        // Dati analisi AI (se presenti, da SegnalazioneStep)
        analisiRichiesta: formData.analisiRichiesta,
        analisiInfo: formData.analisiInfo,
        
        // Dati tipo assistenza
        assistanceTypeId: formData.tipoAssistenza._id,
        assistanceTypeLabel: formData.tipoAssistenza.title || formData.tipoAssistenza.nome,
        assistanceTypeDescription: formData.tipoAssistenza.description,
        // Dati priorità del tipo assistenza
        assistanceTypePriorityId: formData.tipoAssistenza?.priority?._id || '',
        assistanceTypePriorityLabel: formData.tipoAssistenza?.priority?.priorityLabel || '',
        assistanceTypePriorityValue: formData.tipoAssistenza?.priority?.priorityValue || '',
        
        // Dati pagamento e appuntamento
        paymentTypeId: formData.pagamento.metodo,
        paymentTypeLabel: (() => {
          if (!formData.pagamento.metodo) return '';
          const paymentMethod = accountData?.settings?.paymentTypes?.find(m => m._id === formData.pagamento.metodo);
          return paymentMethod ? 
            (paymentMethod.name || paymentMethod.nome || paymentMethod.title || paymentMethod.label) : 
            formData.pagamento.metodo;
        })(),
        appointmentTimeSlot: formData.pagamento.fasciaOraria,
        // Flag teleassistenza
        teleassistenza: formData.pagamento.teleassistenza || false,
        // Il campo plannedDate è opzionale, lo includiamo solo se valorizzato
        ...(formData.pagamento.data ? { plannedDate: formData.pagamento.data } : {}),
        
        // Immagini caricate - usiamo gli URL ottenuti dal server
        uploads: uploadedImageUrls,
        
        // Data creazione
        creationDate: new Date().toISOString(),
        
        // Stato iniziale - ricava lo stato con code 'creata' dalle impostazioni account
        ...(() => {
          // Trova lo stato con code 'creata' nell'array assistanceRequestStatus
          const statoCreata = accountData?.settings?.assistanceRequestStatus?.find(status => status.code === 'creata');
          console.log('Stato "creata" trovato:', statoCreata);
          
          // Se non viene trovato lo stato con code 'creata', interrompi la creazione
          if (!statoCreata || !statoCreata._id) {
            // Imposta un flag di errore che verrà controllato prima dell'invio
            window.statusCreatoNotFound = true;
            console.error('ERRORE: Impossibile trovare lo stato con codice "creata" nelle impostazioni del sistema');
            toast.error('Errore di configurazione: stato "creata" non trovato. Contattare l\'amministratore.');
            return {}; // Questo verrà comunque esteso con gli altri campi
          }
          
          // Restituisci i campi ID e name in un oggetto
          return {
            statusId: statoCreata._id,
            statusLabel: statoCreata.name
          };
        })(),
        
        // GeoData richiesto dallo schema con distanceKm e distanceTime
        geoData: {
          type: "Point",
          coordinates: formData.sistema?.geoData?.coordinates || [10.1234, 45.5678], // Usa coordinate dell'impianto se disponibili
          // Aggiungi sempre distanceKm e distanceTime dai dati dell'impianto se disponibili, altrimenti 0
          distanceKm: formData.sistema?.geoData?.distanceKm !== undefined ? formData.sistema.geoData.distanceKm : 0,
          distanceTime: formData.sistema?.geoData?.distanceTime !== undefined ? formData.sistema.geoData.distanceTime : 0
        }
      };
      
      // Per la modalità edit, dobbiamo includere _id nell'oggetto assistanceRequest
      if (!isCreatingNew && id) {
        assistanceRequest._id = id;
      }
      
      console.log('Oggetto assistanceRequest creato:', JSON.stringify(assistanceRequest));
      
      // 3. Chiamata API principale in formato JSON
      // L'API si aspetta che l'oggetto assistanceRequest sia incluso in un oggetto wrapper
      const apiUrl = isCreatingNew
        ? `${import.meta.env.VITE_API_BASE_URL}/assistanceRequests`
        : `${import.meta.env.VITE_API_BASE_URL}/assistanceRequests`; // Non includiamo l'ID nell'URL per la PATCH
      
      console.log(`Eseguo ${isCreatingNew ? 'POST' : 'PATCH'} a ${apiUrl}`);
      console.log('Corpo della richiesta:', { assistanceRequest });
      
      const response = await fetch(apiUrl, {
        method: isCreatingNew ? 'POST' : 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({ assistanceRequest }) // Incapsulare assistanceRequest in un oggetto
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Errore dal server:', errorData);
        throw new Error(errorData.message || 'Errore durante la creazione della richiesta');
      }
      
      const responseData = await response.json();
      console.log('Risposta API:', responseData);
      
      // Mostra un messaggio diverso in base all'operazione eseguita
      if (isCreatingNew) {
        toast.success('Richiesta di assistenza creata con successo');
        // Reindirizza alla lista dopo la creazione
        navigate('/assistanceRequestsNoAI');
      } else {
        toast.success('Richiesta di assistenza aggiornata con successo');
        // Reindirizza alla lista dopo l'aggiornamento
        navigate('/assistanceRequestsNoAI');
      }
    } catch (error) {
      console.error('Errore durante la creazione della richiesta:', error);
      toast.error('Si è verificato un errore durante la creazione della richiesta');
    } finally {
      setIsSubmitting(false); // Disattiva lo stato di caricamento indipendentemente dal risultato
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <ClienteStep
            formData={formData}
            nextStep={handleNext}
            setFormData={setFormData}
          />
        );
      case 1:
        return (
          <SegnalazioneStep
            formData={formData}
            nextStep={handleNext}
            prevStep={handlePrev}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <ImmaginiStep
            formData={formData}
            nextStep={handleNext}
            prevStep={handlePrev}
            setFormData={setFormData}
          />
        );
      case 3:
        return (
          <TipoAssistenzaStep
            formData={formData}
            nextStep={handleNext}
            prevStep={handlePrev}
            setFormData={setFormData}
          />
        );
      case 4:
        return (
          <PagamentoStep
            formData={formData}
            prevStep={handlePrev}
            nextStep={handleNext}
            setFormData={setFormData}
            confirmSubmit={handleFinalSubmit}
            isSubmitting={isSubmitting}
            isEditMode={isEditMode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          {id ? 'Modifica Richiesta di Assistenza' : 'Nuova Richiesta di Assistenza'}
        </h1>
        
        <div className="mb-8">
          <WizardHeader 
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            zeroBasedIndex={true}
            showConnectingLines={false}
            customStyles={{
              container: { marginTop: 0 }
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          {renderStep()}
        </div>
        
        <div className="lg:col-span-1 sticky top-4" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
          <div className="bg-white rounded-lg shadow p-3 overflow-y-auto" style={{ maxHeight: '100%' }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              color: colors.primary || '#3a5a40'
            }}>
              <i className="fa-solid fa-clipboard-list mr-2"></i>
              Riepilogo Richiesta
            </h2>
            <AssistancePreview
              formData={formData}
              currentStep={currentStep}
              submitForm={handleFinalSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditAssistanceRequestNoAI;